import React from 'react'
import {
    exchangeUserIdentifiers,
    saveAttributesOfCurrentlyLoggedCognitoUser,
    saveTokensOfCurrentlyLoggedCognitoUser,
} from '../../admin/actions/auth-new'
import {
    getLoggedUser,
    saveLoggedUserTokens,
    saveUserIntoLocalStorage,
} from '../../admin/reducers/auth'
import store, { asyncSessionStorage } from '../../admin/store'
import {
    getUrlParameter,
    setEcho,
} from '../../admin/actions'
import Configurator from '../../configurator'
import {
    EVENT_REGISTRATION,
    sendWebViewEvent,
} from '../../events-webview'
import { Auth } from '@aws-amplify/auth'
import { redirectToRemoteLoginBacklink } from '../auth/remote-login-redirect'

const APPLE_REDIRECT_COUNT = 'appleRedirectCount'

/**
 * HOC wrapper for apple sign in
 * @param {React.Component} WrappedComponent
 * @returns {*}
 */
const withAppleSignIn = WrappedComponent => (
    class extends Configurator {
        /**
         * Signs in sportnect user to sportnect API
         *
         * @return {Promise}
         */
        signInAppleUserToSportnectApi = () => {
            const storeToken = store?.getState?.()?.auth?.cognitoStoreTokens?.accessToken

            if (!storeToken) {
                throw new Error('Not tokens in store')
            }

            return this.axios
                .post(`${this.config.REACT_APP_API_URL}api/auth/apple/callback`, {
                    access_token: storeToken,
                })
                .then(response => {
                    store.dispatch({
                        type: 'SET_STORE_TOKENS',
                        tokens: response?.data,
                    })

                    setEcho(response?.data)
                    saveLoggedUserTokens(response?.data)

                    return getLoggedUser()
                        .then(user => saveUserIntoLocalStorage(user, 'email'))
                })
        }

        /**
         * Apple callback reaction
         *
         * @return {Promise}
         */
        appleAuthCallback = () => {
            const error = getUrlParameter('error')

            return asyncSessionStorage
                .getItem(APPLE_REDIRECT_COUNT)
                .then(redirectCount => {
                    if (error === 'invalid_request' && redirectCount === '1') {
                        this.triggerAppleSignIn()
                        throw new Error(null)
                    }

                    return new Promise(resolve => resolve())
                })
                .then(() => saveTokensOfCurrentlyLoggedCognitoUser())
                .then(() => asyncSessionStorage.removeItem(APPLE_REDIRECT_COUNT))
                .then(() => saveAttributesOfCurrentlyLoggedCognitoUser())
                .then(() => this.signInAppleUserToSportnectApi())
                .then(() => exchangeUserIdentifiers())
                .then(() => Promise.resolve().then(() => {
                    const auth = store.getState().auth
                    const tokens = auth.storeTokens || auth.tokens

                    const eventData = {
                        close: true,
                        data: {
                            user: auth?.user,
                            tokens: tokens,
                        },
                    }

                    if (sendWebViewEvent(EVENT_REGISTRATION, eventData) === false) {
                        redirectToRemoteLoginBacklink(tokens)
                            .catch(() => {
                                window.location.href = '/'
                            })
                    }
                }))
                .catch(errorMessage => {
                    if (!errorMessage) {
                        return
                    }

                    Promise.resolve()
                        .then(() => Auth.currentSession())
                        .then(() => window.location.href = '/cognito/oauth/callback?state=provider=SignInWithApple')
                        .catch(() => {
                            setTimeout(() => this.triggerAppleSignIn(), 1000)
                        })
                })
        }

        /**
         * Triggers apple sign in
         */
        triggerAppleSignIn = () => {
            store.dispatch({
                type: 'SET_BUSY_AWS',
                busy: true,
            })

            Promise.resolve()
                .then(() => Auth.configure())
                .then(config => this.processTriggerAppleSignIn(config))
        }

        /**
         * Process trigger apple sign in
         *
         * @param {Object} config
         * @return {Promise<string>}
         */
        processTriggerAppleSignIn = config => {

            const {
                domain,
                responseType,
                scope,
                redirectSignIn,
            } = config.oauth

            const clientId = config.userPoolWebClientId

            return asyncSessionStorage.getItem(APPLE_REDIRECT_COUNT)
                .then(redirectCount => {
                    let newCount = '1'
                    if (redirectCount === '1') {
                        newCount = '2'
                    }

                    return asyncSessionStorage.setItem(APPLE_REDIRECT_COUNT, newCount)
                })
                .then(() => window.location.href = `https://${domain}/oauth2/authorize?identity_provider=SignInWithApple&redirect_uri=${redirectSignIn}&response_type=${responseType}&client_id=${clientId}&scope=${scope.join(' ')}&state=provider=SignInWithApple`)
        }

        /**
         * Render child component with the injected methods
         * @returns {*}
         */
        render = () => (
            <WrappedComponent
                appleAuthCallback={this.appleAuthCallback}
                triggerAppleSignIn={this.triggerAppleSignIn}
                { ...this.props }
            />
        )
    }
)

export default withAppleSignIn
